import React, { useEffect, useState } from "react";
import zxcvbn from "zxcvbn";
import { MIN_ACCEPTABLE_SCORE, MIN_PASSWORD_LENGTH } from "../constants";
import { PasswordStrengthScore } from "../types";

export const getLabel = (score: number) => {
  switch (score) {
    case PasswordStrengthScore.weak:
      return "Weak";
    case PasswordStrengthScore.fair:
      return "Fair";
    case PasswordStrengthScore.good:
      return "Good";
    case PasswordStrengthScore.great:
      return "Great";
    case PasswordStrengthScore.none:
    default:
      return "None";
  }
};

interface Result {
  score?: number;
  feedback: {
    suggestions: string[];
  };
}

interface Strength {
  score: number;
  message: string[];
  label: string;
}

interface PassworthStrengthApi {
  strength: Strength;
}

interface Props {
  password: string;
  personalInfo: string[];
  children: (props: PassworthStrengthApi) => React.ReactNode;
}

const PasswordStrengthContainer: React.FC<Props> = ({
  password,
  personalInfo,
  children,
}) => {
  const result: Result = zxcvbn(password);

  const [strength, setStrength] = useState<Strength>({
    score: 0,
    message: [],
    label: getLabel(0),
  });

  useEffect(() => {
    const baseScore =
      password.length > 0 ? result.score || PasswordStrengthScore.weak : 0;

    const personalInfoPatterns = personalInfo.map(
      (item) => new RegExp(item, "i")
    );
    const hasPersonalInfo = personalInfoPatterns.some((pattern) =>
      pattern.test(password)
    );

    const isLongEnough = password.length >= MIN_PASSWORD_LENGTH;

    let altScore = PasswordStrengthScore.fair;
    if (hasPersonalInfo) {
      altScore = PasswordStrengthScore.weak;
    } else if (isLongEnough) {
      altScore = PasswordStrengthScore.great;
    }

    const score = Math.min(baseScore, altScore);
    let message = result.feedback.suggestions;
    if (hasPersonalInfo) {
      message = [
        "Your password must not include your name or other personal information.",
      ];
    } else if (!isLongEnough) {
      message = [
        `Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      ];
    } else if (message.length === 0 && score < MIN_ACCEPTABLE_SCORE) {
      message = [
        "Avoid using common phrases or names. Try adding more characters or using a strong password generator.",
      ];
    }

    setStrength({ score, message, label: getLabel(score) });
  }, [password]);

  return children({ strength });
};

export default PasswordStrengthContainer;
